import { AlertTriangle, Loader2, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDeletableSummary, useDeleteStorageSubtree } from '@/hooks/useStorage'
import { STORAGE_NODE_KIND_LABELS, type StorageNodeKind } from '@/types/storage'

/**
 * Confirm removing a storage unit and everything describing it.
 *
 * States the size of the thing before anybody agrees to it — "delete 250 nodes"
 * is a decision, "delete" is not — and is honest about what survives: the
 * equipment record stays, only the storage layout goes.
 *
 * The button is disabled from the server's own answer rather than a guess here.
 * The server re-checks under a lock anyway, so this only spares the user a
 * refusal they can already see coming.
 */
export function DeleteStorageUnitDialog({
  unitId,
  unitLabel,
  onClose,
  onDeleted,
}: {
  /** null keeps the dialog shut and its subtree scan unasked. */
  unitId: string | null
  unitLabel: string
  onClose: () => void
  onDeleted?: () => void
}) {
  const summary = useDeletableSummary(unitId)
  const remove = useDeleteStorageSubtree()

  const data = summary.data
  const kinds = Object.entries(data?.byKind ?? {}).sort(([a], [b]) => a.localeCompare(b))

  return (
    <Dialog
      open={unitId !== null}
      onOpenChange={(open) => {
        if (!open) {
          remove.reset()
          onClose()
        }
      }}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='flex items-center gap-2'>
            <Trash2 className='h-5 w-5 text-destructive' />
            Delete {unitLabel}?
          </DialogTitle>
          <DialogDescription>
            The equipment record is kept — only the storage layout is deleted.
          </DialogDescription>
        </DialogHeader>

        {summary.isLoading ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
            Checking what is inside…
          </p>
        ) : summary.error ? (
          <p className='text-sm text-destructive'>{String(summary.error.message)}</p>
        ) : data ? (
          <div className='space-y-3'>
            <div className='rounded-md border bg-muted/30 px-3 py-2 text-sm'>
              <p className='font-medium'>This removes {data.nodes} node(s):</p>
              <p className='mt-1 text-muted-foreground'>
                {kinds.length === 0
                  ? 'Nothing is described inside it yet.'
                  : kinds
                      .map(
                        ([kind, n]) =>
                          `${n} ${STORAGE_NODE_KIND_LABELS[kind as StorageNodeKind] ?? kind}`,
                      )
                      .join(' · ')}
              </p>
            </div>

            {data.canDelete ? (
              <p className='text-sm text-muted-foreground'>Nothing is stored in any of them.</p>
            ) : (
              // Not a validation message: it is the reason, with the number, so
              // the next step is obvious rather than a guess.
              <div className='flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                <span>
                  Still in use: {data.occupants} lot(s) stored inside
                  {data.pickLines > 0 && ` and ${data.pickLines} pick-list line(s) pointing at it`}.
                  Move the stock out first.
                </span>
              </div>
            )}
          </div>
        ) : null}

        {remove.error && <p className='text-sm text-destructive'>{String(remove.error.message)}</p>}

        <div className='flex justify-end gap-2 pt-1'>
          <Button variant='outline' onClick={() => { remove.reset(); onClose() }}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            className='gap-1.5'
            disabled={!data?.canDelete || remove.isPending}
            onClick={() =>
              unitId &&
              remove.mutate(unitId, {
                onSuccess: () => {
                  onDeleted?.()
                  onClose()
                },
              })
            }
          >
            <Trash2 className='h-4 w-4' />
            {remove.isPending ? 'Deleting…' : `Delete ${data?.nodes ?? ''} node(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
